import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";

const gridDrift = keyframes`
  0% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.45;
  }
  100% {
    opacity: 0.3;
  }
`;

export const HomeScene = styled.main`
  position: relative;
  flex: 1;
  background:
    radial-gradient(
      ellipse 100% 80% at 0% -10%,
      rgba(79, 70, 229, 0.14),
      transparent 55%
    ),
    radial-gradient(
      circle at 100% 0%,
      rgba(34, 211, 238, 0.06),
      transparent 42%
    ),
    #060912;
  overflow: hidden;

  @media (prefers-reduced-motion: reduce) {
    .motion-grid {
      animation: none !important;
    }
  }
`;

export const HeroBackdrop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: min(88vh, 820px);
  min-height: 460px;
  overflow: hidden;
  z-index: 0;
  pointer-events: none;
`;

export const NoiseLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
  opacity: 0.025;
  mix-blend-mode: overlay;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
`;

export const GridOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
  opacity: 0.06;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
  background-size: 72px 72px;
  mask-image: radial-gradient(ellipse 75% 65% at 50% 35%, black 15%, transparent 72%);
  animation: ${gridDrift} 14s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.08;
  }
`;

export const SceneGlow = styled.div`
  position: absolute;
  inset: -25%;
  z-index: 1;
  pointer-events: none;
  filter: blur(80px);
  mix-blend-mode: normal;
  opacity: 0.32;
  background:
    radial-gradient(
      circle at 15% 25%,
      rgba(99, 102, 241, 0.08),
      transparent 50%
    ),
    radial-gradient(
      circle at 85% 15%,
      rgba(34, 211, 238, 0.05),
      transparent 46%
    );
  animation: pulseGlow 14s ease-in-out infinite;

  @media (max-width: 768px) {
    filter: blur(64px);
    opacity: 0.22;
  }

  @keyframes pulseGlow {
    0%,
    100% {
      transform: scale(1) translate(0, 0);
    }
    50% {
      transform: scale(1.02) translate(0.2%, -0.2%);
    }
  }
`;

export const Banner = styled.div`
  position: relative;
  z-index: 1;
  min-height: 100vh;
  padding: 80px 0 28px;

  @media (max-width: 1024px) {
    min-height: auto;
    padding: 70px 0 28px;
  }

  @media (max-width: 768px) {
    padding: 62px 0 24px;
  }

  @media (max-width: 576px) {
    padding: 56px 0 22px;
  }
`;

/* ——— Unified hero ——— */
export const HeroPanel = styled.div`
  position: relative;
  width: 100%;
  margin: 0;
  padding: 0;
  background: none;
  border: none;
  box-shadow: none;
  overflow: visible;
`;

export const HeroShell = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  box-sizing: border-box;
  padding: clamp(4px, 1.5vw, 12px) 0 0;

  @media (max-width: 576px) {
    padding: 0;
  }
`;

export const HeroThreeHost = styled.div`
  position: absolute;
  inset: -12% -8% -18%;
  z-index: 0;
  pointer-events: none;
  opacity: 0.92;

  canvas {
    display: block;
    width: 100% !important;
    height: 100% !important;
  }

  @media (max-width: 768px) {
    inset: -8% -12% -14%;
    opacity: 0.85;
  }

  @media (prefers-reduced-motion: reduce) {
    opacity: 0.75;
  }
`;

export const HeroIntroColumn = styled.div`
  padding-right: 0;
  max-width: 100%;

  @media (min-width: 992px) {
    padding-right: 12px;
  }
`;

export const HeroVisualColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

export const PortfolioBadge = styled.div`
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  letter-spacing: 0.11em;
  text-transform: uppercase;
  font-weight: 600;
  color: #c7d2fe;
  margin-bottom: 16px;
  padding: 7px 14px;
  border-radius: 999px;
  border: 1px solid rgba(129, 140, 248, 0.35);
  background: rgba(79, 70, 229, 0.12);

  @media (max-width: 576px) {
    font-size: 10px;
    padding: 6px 12px;
    margin-bottom: 14px;
  }
`;

export const HeroName = styled.h1`
  margin: 0 0 12px;
  font-size: clamp(1.75rem, 3.5vw, 2.5rem);
  font-weight: 700;
  letter-spacing: -0.035em;
  line-height: 1.08;
  color: #f8fafc;

  @media (max-width: 576px) {
    font-size: 1.6rem;
  }
`;

export const HeroRole = styled.p`
  margin: 0 0 16px;
  font-size: clamp(1.02rem, 1.35vw, 1.15rem);
  font-weight: 500;
  line-height: 1.55;
  color: #94a3b8;
  max-width: 36rem;
`;

export const SkillsLabel = styled.span`
  display: block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.11em;
  text-transform: uppercase;
  color: rgba(148, 163, 184, 0.85);
  margin-bottom: 10px;
`;

export const StatsBar = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px 16px;
  padding: 4px 0 16px;
  margin-bottom: 0;
  border-top: 1px solid rgba(148, 163, 184, 0.12);

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
    gap: 0;
    padding: 4px 0 12px;
  }
`;

export const HeroMetricsStrip = styled.div`
  margin-top: clamp(16px, 2.5vw, 24px);
  padding: 14px 0 16px;
  border-top: 1px solid rgba(148, 163, 184, 0.12);

  ${StatsBar} {
    border-top: none;
    padding-top: 0;
    margin-bottom: 0;
    padding-bottom: 16px;
    border-bottom: 1px solid rgba(148, 163, 184, 0.1);
  }

  > div:last-child {
    padding-top: 14px;
  }

  @media (max-width: 576px) {
    padding: 12px 0 14px;

    > div:last-child {
      padding-top: 12px;
    }
  }
`;

export const StatBarItem = styled.div`
  .value {
    font-size: clamp(1.2rem, 2vw, 1.5rem);
    font-weight: 700;
    color: #f1f5f9;
    letter-spacing: -0.02em;
    line-height: 1.15;
    font-variant-numeric: tabular-nums;
  }

  .label {
    margin-top: 6px;
    font-size: 12px;
    line-height: 1.45;
    color: rgba(148, 163, 184, 0.9);
  }

  @media (min-width: 521px) {
    padding: 4px 8px 4px 0;

    &:not(:first-child) {
      padding-left: 20px;
      border-left: 1px solid rgba(148, 163, 184, 0.1);
    }
  }

  @media (max-width: 520px) {
    padding: 12px 2px;

    &:not(:last-child) {
      border-bottom: 1px solid rgba(148, 163, 184, 0.08);
    }
  }
`;

export const LeadText = styled.p`
  color: rgba(226, 232, 240, 0.9);
  font-size: clamp(15px, 1.4vw, 17px);
  line-height: 1.65;
  max-width: 38rem;
  margin-bottom: 20px;

  @media (max-width: 576px) {
    font-size: 14px;
    margin-bottom: 16px;
  }
`;

export const CTAGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 0;

  a {
    text-decoration: none;
    border-radius: 999px;
    padding: 11px 22px;
    font-weight: 600;
    font-size: 14px;
    transition:
      background 0.2s ease,
      color 0.2s ease,
      border-color 0.2s ease,
      transform 0.2s ease;
  }

  .primary-cta {
    color: #fff;
    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
    border: 1px solid transparent;
    box-shadow: 0 4px 20px rgba(79, 70, 229, 0.35);
  }

  .primary-cta:hover {
    background: linear-gradient(135deg, #4f46e5 0%, #4338ca 100%);
    color: #fff;
    transform: translateY(-1px);
  }

  .secondary-cta {
    color: #e2e8f0;
    border: 1px solid rgba(148, 163, 184, 0.35);
    background: rgba(15, 23, 42, 0.5);
  }

  .secondary-cta:hover {
    border-color: #94a3b8;
    color: #f8fafc;
    background: rgba(30, 41, 59, 0.55);
  }

  @media (max-width: 768px) {
    gap: 10px;

    a {
      width: 100%;
      text-align: center;
      padding: 11px 18px;
      font-size: 13px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    a:hover {
      transform: none;
    }
  }
`;

export const PillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 10px;
  margin-bottom: 0;
  max-width: 100%;

  span {
    font-size: 12px;
    font-weight: 500;
    color: rgba(226, 232, 240, 0.92);
    border: 1px solid rgba(148, 163, 184, 0.2);
    border-radius: 8px;
    padding: 6px 12px;
    background: rgba(15, 23, 42, 0.55);
  }

  span:hover {
    border-color: rgba(129, 140, 248, 0.4);
    background: rgba(30, 27, 75, 0.25);
  }

  @media (max-width: 576px) {
    span {
      font-size: 11px;
      padding: 5px 10px;
    }
  }
`;

export const ProfileWrap = styled.div`
  position: relative;
  z-index: 3;
  isolation: isolate;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  overflow: visible;

  @media (max-width: 992px) {
    min-height: 260px;
    margin-top: 8px;
  }

  @media (max-width: 768px) {
    min-height: 220px;
  }

  @media (max-width: 576px) {
    min-height: 190px;
  }
`;

export const OrbitRing = styled.div`
  position: absolute;
  z-index: 1;
  width: 248px;
  height: 248px;
  border: 1px solid rgba(129, 140, 248, 0.1);
  border-radius: 50%;
  animation: spinRing 26s linear infinite;

  &::after {
    content: "";
    position: absolute;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: linear-gradient(135deg, #22d3ee, #818cf8);
    top: 14px;
    left: 62px;
    box-shadow: 0 0 12px rgba(99, 102, 241, 0.5);
  }

  &.two {
    width: 300px;
    height: 300px;
    border-color: rgba(251, 146, 60, 0.08);
    animation-duration: 38s;
    animation-direction: reverse;
  }

  @keyframes spinRing {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @media (max-width: 768px) {
    width: 188px;
    height: 188px;

    &.two {
      width: 228px;
      height: 228px;
    }
  }

  @media (max-width: 576px) {
    width: 162px;
    height: 162px;

    &.two {
      width: 198px;
      height: 198px;
    }
  }
`;

export const ProfileGlow = styled.div`
  position: absolute;
  z-index: 1;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(99, 102, 241, 0.18),
    rgba(34, 211, 238, 0.05) 50%,
    transparent 72%
  );
  filter: blur(18px);
`;

export const ProfileFrame = styled.div`
  position: relative;
  width: 216px;
  height: 216px;
  border-radius: 50%;
  border: 3px solid rgba(148, 163, 184, 0.22);
  overflow: hidden;
  background: #0f172a;
  z-index: 2;
  box-shadow:
    0 12px 40px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.06) inset;

  img {
    position: relative;
    z-index: 1;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
    object-position: center top;
  }

  @media (max-width: 768px) {
    width: 176px;
    height: 176px;
  }

  @media (max-width: 576px) {
    width: 152px;
    height: 152px;
  }
`;

export const HeroMeta = styled.p`
  text-align: center;
  color: rgba(148, 163, 184, 0.92);
  font-size: 13px;
  margin: 18px 0 0;
  letter-spacing: 0.01em;
  line-height: 1.5;
  max-width: 20rem;

  @media (max-width: 768px) {
    margin-top: 14px;
    font-size: 12px;
  }
`;

/* ——— Inline shadow-map demo ——— */
export const HomeShadowDemoSection = styled.section`
  width: 100%;
  box-sizing: border-box;
  margin-top: 28px;
  padding: 22px 0 12px;
  border-top: 1px solid rgba(148, 163, 184, 0.12);
`;

export const HomeShadowDemoHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem 1rem;
  margin-bottom: 14px;

  .home-shadow-demo-link {
    font-size: 13px;
    font-weight: 600;
    color: #a5b4fc;
    text-decoration: none;
    white-space: nowrap;

    &:hover {
      color: #c7d2fe;
    }
  }
`;

/* ——— Capabilities ——— */
export const CapabilitiesSection = styled.section`
  position: relative;
  z-index: 2;
  width: 100%;
  box-sizing: border-box;
  margin-top: 28px;
  padding: 20px 0 20px;
  border-top: 1px solid rgba(148, 163, 184, 0.12);

  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding: 18px 0 18px;
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
    margin-top: 22px;
    padding: 18px 0 16px;
  }
`;

export const CapabilitiesLead = styled.div`
  grid-column: 1 / -1;
  margin-bottom: 4px;
  padding: 4px 4px 18px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);

  @media (max-width: 520px) {
    padding: 2px 2px 16px;
  }
`;

export const CapabilitiesTitle = styled.h2`
  margin: 0;
  font-size: clamp(1.125rem, 2vw, 1.35rem);
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #f1f5f9;
  line-height: 1.35;
  max-width: 28rem;
`;

export const CapabilityTile = styled.div`
  padding: 16px 18px;
  background: transparent;
  border: none;
  border-radius: 0;
  border-right: 1px solid rgba(148, 163, 184, 0.1);

  &:last-child {
    border-right: none;
  }

  .cap-icon {
    font-size: 20px;
    line-height: 1;
    margin-bottom: 10px;
    color: #a5b4fc;
  }

  h4 {
    margin: 0 0 6px;
    font-size: 15px;
    font-weight: 600;
    color: #f8fafc;
    letter-spacing: -0.02em;
  }

  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.55;
    color: rgba(203, 213, 225, 0.9);
  }

  @media (max-width: 992px) {
    border-right: none;
    border-bottom: 1px solid rgba(148, 163, 184, 0.08);
    padding: 14px 4px;

    &:last-child {
      border-bottom: none;
    }
  }

  @media (max-width: 520px) {
    padding: 14px 2px;
  }
`;

/* ——— Featured case studies ——— */
export const FeaturedSection = styled.section`
  position: relative;
  z-index: 2;
  width: 100%;
  box-sizing: border-box;
  margin-top: clamp(1.75rem, 4vw, 2.75rem);
  padding: clamp(1.75rem, 4vw, 2.75rem) 0 clamp(2rem, 4vw, 2.5rem);
  border-top: 1px solid rgba(148, 163, 184, 0.1);
  background: linear-gradient(
    180deg,
    rgba(99, 102, 241, 0.07) 0%,
    transparent 42%,
    rgba(15, 23, 42, 0.35) 100%
  );
  border-radius: 0 0 20px 20px;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(129, 140, 248, 0.35) 50%,
      transparent
    );
    pointer-events: none;
  }

  @media (max-width: 768px) {
    margin-top: 1.5rem;
    padding: 1.5rem 0 1.75rem;
    border-radius: 0;
  }
`;

export const FeaturedSectionHead = styled.div`
  margin-bottom: clamp(1.25rem, 3vw, 1.75rem);
  padding: 0 4px clamp(1rem, 2vw, 1.25rem);
  max-width: 44rem;

  h2 {
    margin-bottom: 0;
    font-size: clamp(1.2rem, 2.6vw, 1.55rem);
    line-height: 1.3;
    max-width: 38rem;
    background: linear-gradient(135deg, #f8fafc 0%, #c7d2fe 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }

  @media (max-width: 576px) {
    margin-bottom: 1.1rem;
    padding: 0 2px 0.85rem;

    h2 {
      font-size: clamp(1.08rem, 4.2vw, 1.35rem);
    }
  }
`;

export const SectionLabel = styled.div`
  display: block;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #a5b4fc;
  margin-bottom: 8px;
  font-weight: 600;
`;

export const SectionTitle = styled.h2`
  margin: 0;
  color: #f1f5f9;
  font-size: clamp(1.1rem, 2vw, 1.35rem);
  font-weight: 600;
  line-height: 1.4;
  max-width: 34rem;
  letter-spacing: -0.02em;
`;

export const FeaturedGrid = styled.div`
  display: grid;
  gap: clamp(0.85rem, 2vw, 1.15rem);
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: stretch;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    max-width: 520px;
    margin: 0 auto;
  }

  @media (max-width: 576px) {
    max-width: none;
    gap: 0.75rem;
  }
`;

export const FeaturedCard = styled(Link)`
  position: relative;
  box-sizing: border-box;
  height: 100%;
  min-height: 240px;
  display: flex;
  flex-direction: column;
  padding: clamp(1.15rem, 2.5vw, 1.45rem);
  text-decoration: none;
  color: inherit;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: linear-gradient(
    155deg,
    rgba(30, 41, 59, 0.55) 0%,
    rgba(15, 23, 42, 0.88) 48%,
    rgba(7, 10, 18, 0.95) 100%
  );
  box-shadow:
    0 4px 24px rgba(0, 0, 0, 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.06);
  overflow: hidden;
  isolation: isolate;
  transition:
    transform 0.38s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.38s ease,
    border-color 0.28s ease;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: radial-gradient(
      120% 80% at 100% 0%,
      rgba(99, 102, 241, 0.12),
      transparent 55%
    );
    opacity: 0;
    transition: opacity 0.35s ease;
    pointer-events: none;
    z-index: 0;
  }

  &:hover {
    transform: translateY(-5px);
    border-color: rgba(165, 180, 252, 0.38);
    box-shadow:
      0 22px 56px rgba(0, 0, 0, 0.38),
      0 0 0 1px rgba(129, 140, 248, 0.12),
      inset 0 1px 0 rgba(255, 255, 255, 0.08);
  }

  &:hover::after {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid rgba(125, 211, 252, 0.9);
    outline-offset: 3px;
  }

  .featured-card-index {
    position: absolute;
    top: clamp(0.65rem, 2vw, 0.85rem);
    right: clamp(0.65rem, 2vw, 0.9rem);
    font-size: clamp(2.25rem, 5vw, 2.75rem);
    font-weight: 750;
    line-height: 1;
    letter-spacing: -0.04em;
    color: rgba(148, 163, 184, 0.12);
    pointer-events: none;
    z-index: 1;
    user-select: none;
  }

  h3 {
    position: relative;
    z-index: 1;
    margin: 0 0 0.5rem;
    padding-right: 2.5rem;
    font-size: clamp(1rem, 1.35vw, 1.125rem);
    font-weight: 650;
    color: #f8fafc;
    letter-spacing: -0.02em;
    line-height: 1.25;
  }

  p {
    position: relative;
    z-index: 1;
    margin: 0 0 1rem;
    flex: 1;
    font-size: clamp(13px, 1.15vw, 14px);
    line-height: 1.62;
    color: rgba(203, 213, 225, 0.9);
  }

  .tag-row {
    position: relative;
    z-index: 1;
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    margin-bottom: 1rem;

    span {
      border: 1px solid rgba(129, 140, 248, 0.28);
      border-radius: 999px;
      font-size: 10px;
      font-weight: 650;
      color: rgba(199, 210, 254, 0.95);
      padding: 0.3rem 0.55rem;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      background: rgba(99, 102, 241, 0.12);
    }
  }

  .featured-cta {
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    margin-top: auto;
    font-size: 0.8125rem;
    font-weight: 650;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #a5b4fc;
    transition: color 0.25s ease;
  }

  .featured-cta::after {
    content: "→";
    font-size: 1rem;
    line-height: 1;
    transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1);
  }

  &:hover .featured-cta {
    color: #e0e7ff;
  }

  &:hover .featured-cta::after {
    transform: translateX(5px);
  }

  @media (max-width: 992px) {
    min-height: 0;
  }

  @media (max-width: 576px) {
    padding: 1.05rem 1rem 1.15rem;
    border-radius: 16px;

    .featured-card-index {
      font-size: 2rem;
      top: 0.55rem;
      right: 0.55rem;
    }

    p {
      font-size: 13px;
    }
  }
`;
